import type { SystemFlow } from './index';

const X = (n: number) => n * 380;
const Y_MAIN = 80;

export const cycleExecutionFlow: SystemFlow = {
  id: 'cycle-execution',
  title: 'Cycle Execution',
  description: 'How a facilitator creates a cycle, configures tracks, runs ranking sessions, and publishes priority lists.',
  audience: 'Facilitators, Admins',
  nodes: [
    {
      id: 'facilitator',
      type: 'actor',
      label: 'Facilitator',
      description: 'Admin or cycle owner who configures and runs the cycle',
      position: { x: X(0), y: Y_MAIN },
    },
    {
      id: 'createCycle',
      type: 'process',
      label: 'Create Cycle',
      description: 'Cycle created with name, cadence (one-time / scheduled / always-on), and org scope.',
      domain: 'cycle',
      position: { x: X(1), y: Y_MAIN },
    },
    {
      id: 'configureTracks',
      type: 'process',
      label: 'Configure Tracks',
      description: 'Each Track defines a comparability lane: one item category × one altitude level.',
      domain: 'cycle',
      position: { x: X(2), y: Y_MAIN },
    },
    {
      id: 'trackReadiness',
      type: 'process',
      label: 'Track Readiness',
      description: 'Six conditions evaluated: participation, pool, criteria, scales, values, ranking tool.',
      domain: 'cycle',
      position: { x: X(3), y: Y_MAIN },
    },
    {
      id: 'openSession',
      type: 'process',
      label: 'Open Session',
      description: 'Session launched for a Track. Participants invited. Items locked for comparison.',
      domain: 'cycle',
      position: { x: X(4), y: Y_MAIN },
    },
    {
      id: 'rankItems',
      type: 'process',
      label: 'Rank Items',
      description: 'Participants apply selected tool. Each item scored. Consensus or aggregation applied.',
      domain: 'cycle',
      position: { x: X(5), y: Y_MAIN },
    },
    {
      id: 'closeSession',
      type: 'data',
      label: 'Session Complete',
      description: 'Results locked. Ranked list available. Track marked complete.',
      domain: 'cycle',
      position: { x: X(6), y: Y_MAIN },
    },
    {
      id: 'cycleResults',
      type: 'process',
      label: 'Cycle Results',
      description: 'All track results assembled. Cross-track view available. Ready for governance review.',
      domain: 'governance',
      position: { x: X(7), y: Y_MAIN },
    },
    {
      id: 'publish',
      type: 'data',
      label: 'Published Cycle',
      description: 'Priority lists published per track. Cycle archived. Next cycle can begin.',
      domain: 'output',
      position: { x: X(8), y: Y_MAIN },
    },
  ],
  edges: [
    {
      id: 'e-facilitator-createCycle',
      from: 'facilitator',
      to: 'createCycle',
      sourceLabel: 'Cycle Intent',
      targetLabel: 'Draft Cycle',
    },
    {
      id: 'e-createCycle-configureTracks',
      from: 'createCycle',
      to: 'configureTracks',
      sourceLabel: 'Empty Cycle',
      targetLabel: 'Track Definitions',
    },
    {
      id: 'e-configureTracks-trackReadiness',
      from: 'configureTracks',
      to: 'trackReadiness',
      sourceLabel: 'Configured Track',
      targetLabel: 'Readiness Evaluation',
    },
    {
      id: 'e-trackReadiness-openSession',
      from: 'trackReadiness',
      to: 'openSession',
      sourceLabel: 'Ready Track',
      targetLabel: 'Launchable Session',
      logic: 'all 6 conditions met',
    },
    {
      id: 'e-openSession-rankItems',
      from: 'openSession',
      to: 'rankItems',
      sourceLabel: 'Active Session',
      targetLabel: 'Ranking Context',
    },
    {
      id: 'e-rankItems-closeSession',
      from: 'rankItems',
      to: 'closeSession',
      sourceLabel: 'Participant Votes',
      targetLabel: 'Aggregated Results',
    },
    {
      id: 'e-closeSession-cycleResults',
      from: 'closeSession',
      to: 'cycleResults',
      sourceLabel: 'Track Result',
      targetLabel: 'Cycle Contribution',
    },
    {
      id: 'e-cycleResults-publish',
      from: 'cycleResults',
      to: 'publish',
      sourceLabel: 'Draft Priority List',
      targetLabel: 'Ratified Publication',
    },
  ],
};
